"use client";

import ColorfulSlider from "@/components/colorful/colorful-slider/colorful-slider";
import FilterBar from "@/components/filter-bar/filter-bar";
import { getCategoriesSliders } from "@/lib/api/categories/sliders";
import Link from "next/link";
import { useEffect, useState } from "react";
import useSWR from "swr";
import Loading from "../_partials/loading/loading";
import { GenreModel } from "../_types/genre-model";
import { Sort } from "../_types/sort";

const CategorySliders = ({
	initialSorts,
	initialSlides,
	initialGenre,
	initialGenres,
	initialSearchTerm,
	initialSort,
	kindObj,
}: {
	initialSorts: any[];
	initialSlides: any[];
	initialSearchTerm?: string;
	initialSort?: Sort;
	initialGenre?: GenreModel;
	initialGenres?: any[];
	kindObj: any;
}) => {
	const [selectedGenre, setSelectedGenre] = useState(initialGenre ?? undefined);
	const [selectedSort, setSelectedSort] = useState<Sort | undefined>(
		initialSort ?? undefined
	);
	const [searchTerm, setSearchTerm] = useState(initialSearchTerm || "");

	const {
		data: slides,
		mutate,
		isLoading,
	} = useSWR(
		[
			"category-sliders",
			kindObj.kind,
			selectedSort?.value,
			searchTerm,
			selectedGenre?.id,
		],
		() =>
			getCategoriesSliders(
				kindObj.kind,
				selectedGenre?.id,
				searchTerm,
				selectedSort
			),
		{ fallbackData: initialSlides, revalidateOnMount: false }
	);

	useEffect(() => {
		const query = new URLSearchParams();

		searchTerm && query.append("title", searchTerm);
		selectedGenre && query.append("genre", selectedGenre.id);
		if (selectedSort) {
			query.append("sortBy", selectedSort.sortBy);
			query.append("direction", selectedSort.direction);
		}

		const qs = query.toString();
		const newUrl = qs
			? `${window.location.pathname}?${qs}`
			: window.location.pathname;

		window.history.replaceState(null, "", newUrl);

		mutate();
	}, [searchTerm, selectedSort, selectedGenre, mutate]);

	return (
		<>
			<div className="w-full flex flex-wrap justify-between items-center px-[24px] md:px-[80px] py-4 gap-4">
				<h2 className="text-white text-xl md:text-[32px] font-bold">
					{kindObj?.name}
				</h2>
				<FilterBar
					showSearch={true}
					showSort={true}
					showGenre={true}
					searchTerm={searchTerm}
					setSearchTerm={setSearchTerm}
					initialSorts={initialSorts}
					initialGenres={initialGenres}
					activeGenre={initialGenre}
					activeSort={initialSort}
					setSort={setSelectedSort}
					setGenre={setSelectedGenre}
				/>
			</div>
			<div className="my-6">
				{isLoading ? (
					<div className="w-full h-full min-h-screen flex justify-center items-center">
						<Loading />
					</div>
				) : (
					<>
						{slides
							.slice()
							.sort((a: any, b: any) => {
								const orderA = a?.category?.order ?? Number.MAX_SAFE_INTEGER;
								const orderB = b?.category?.order ?? Number.MAX_SAFE_INTEGER;
								return orderA - orderB;
							})
							.map((data: any, index: number) => {
								return (
									<div
										className={"w-full px-[24px] md:px-[80px] pt-1"}
										key={index}
									>
										<div className={"flex items-center justify-between"}>
											<h2 className="text-white text-lg font-semibold mb-[10px] md:mb-6 text-right md:text-[24px]">
												{data?.category?.persianName}
											</h2>
											<Link
												className={
													"text-xs md:text-[18px] font-semiBold text-custom-pink underline hover:opacity-70"
												}
												href={`/${kindObj?.kind}/categories/${data?.category?.id}`}
											>
												مشاهده همه
											</Link>
										</div>
										<ColorfulSlider
											id={`subcategory-${index}`}
											slides={data.content.slice(0, 10)}
											kind={kindObj.kind}
											activeGenre={initialGenre?.persianName}
											centeredSlides={false}
										/>
									</div>
								);
							})}
					</>
				)}
			</div>
		</>
	);
};

export default CategorySliders;
